import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    styled,
    PaginationProps,
    TableSortLabel
} from '@mui/material';

import theme from '../../../theme';
import { ClinicalReviewModel } from '../../../lib/models/ClinicalReviewModel';
import ClinicalReviewItem from './ClinicalReviewItem';
import Pagination from './Pagination';
import { useScreenLargerThan } from '../../../lib/hooks/breakpoint-hooks';
import { useState } from 'react';
import DatagridNoRowsComponent from '../../DatagridNoRowsComponent';
import { clinicalReviewListingColumns } from '../../../lib/clinical-review-listing-columns';

export type ColKeys =
    | 'dueDate'
    | 'assignedDate'
    | 'caseNumber'
    | 'hasNewMessage'
    | 'caseStage'
    | 'inProgress';

export type ClinicalReivewListingProps = {
    data: ClinicalReviewModel[] | undefined;
    paginationProps: PaginationProps;
    onSort: (
        colKey: 'dueDate' | 'assignedDate' | 'hasNewMessage',
        direction: 'asc' | 'desc'
    ) => void;
    removeColumns?: ColKeys[];
};

const ClinicalReviewListing = ({
    data,
    paginationProps,
    onSort,
    removeColumns
}: ClinicalReivewListingProps) => {
    const isLargerThanMedium = useScreenLargerThan('md');
    const [colKey, setColKey] = useState<'dueDate' | 'assignedDate' | 'hasNewMessage' | null>(null);
    const [direction, setDirection] = useState<'asc' | 'desc'>('asc');

    const sortHandler = (refColKey: 'dueDate' | 'assignedDate' | 'hasNewMessage') => {
        const refDirection = direction === 'asc' ? 'desc' : 'asc';

        setColKey(refColKey);
        setDirection(refDirection);

        onSort(refColKey, refDirection);
    };

    const TableHeaderCell = styled(TableCell)({
        color: theme.palette.cardSubtitle.main,
        fontSize: 13,
        borderBottom: 'none',
        padding: isLargerThanMedium ? '1rem' : '0.188rem'
    });

    const columns = clinicalReviewListingColumns(isLargerThanMedium).filter(
        (column) => !removeColumns?.includes(column.key as ColKeys)
    );

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => {
                            if (column.hideOnSmallScreen && !isLargerThanMedium) return null;
                            return (
                                <TableHeaderCell
                                    key={column.key}
                                    width={column.width}
                                    align={
                                        (column.align as
                                            | 'left'
                                            | 'center'
                                            | 'right'
                                            | 'justify'
                                            | 'inherit'
                                            | undefined) || 'left'
                                    }>
                                    {column.sortable ? (
                                        <TableSortLabel
                                            direction={direction}
                                            active={colKey === column.key}
                                            onClick={() => {
                                                sortHandler(
                                                    column.key as
                                                        | 'dueDate'
                                                        | 'assignedDate'
                                                        | 'hasNewMessage'
                                                );
                                            }}>
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableHeaderCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data &&
                        data.length > 0 &&
                        data.map((item, index) => (
                            <ClinicalReviewItem
                                key={index}
                                item={item}
                                removeColumns={removeColumns}
                            />
                        ))}
                </TableBody>
            </Table>
            {data && data.length < 1 && (
                <DatagridNoRowsComponent
                    text="No reviews"
                    text-props={{ fontSize: 14, color: (t) => t.palette.action.active }}
                    fontSize={30}
                    margin={3}
                />
            )}
            {data && data.length > 0 && <Pagination shape="rounded" {...paginationProps} />}
        </TableContainer>
    );
};

export default ClinicalReviewListing;
